import clsx from 'clsx'
import { Children, type CSSProperties, type ReactNode } from 'react'

import { PageMeta } from '@/shared/lib/meta/page-meta'
import {
  ProgressBar,
  type ProgressBarSize,
  type ProgressBarVariant
} from '@/shared/ui/ProgressBar'

import { PageBackButton } from './PageBackButton'
import styles from './PageHero.module.scss'

// ── Sub-components (card variants) ────────────────────────────

const Title = ({ children }: { children: ReactNode }) => (
  <p className={styles.heroTitle}>{children}</p>
)
Title.displayName = 'PageHero.Title'

type SubtitleProps = {
  children: ReactNode
  /**
   * Nº de linhas antes de truncar com reticências. Sem `clamp`, mantém o
   * layout de linha única (`display: flex`, ícone + texto) usado pelas
   * subtitles curtas — jornadas/missões/quizzes na listagem, contador de
   * fases, recompensa inline. Com `clamp`, vira bloco de texto livre
   * (descrição de recurso vinda de CMS) e passa a ter teto de altura —
   * ver MASTER §7 L9.
   */
  clamp?: number
}

const hasSubtitleContent = (children: ReactNode) =>
  Children.toArray(children).some(
    child => child !== null && child !== undefined && child !== false
  )

const Subtitle = ({ children, clamp }: SubtitleProps) => {
  if (!hasSubtitleContent(children)) return null

  return (
    <p
      className={clsx(styles.heroSubtitle, clamp && styles.heroSubtitleClamp)}
      style={clamp ? ({ WebkitLineClamp: clamp } as CSSProperties) : undefined}
    >
      {children}
    </p>
  )
}
Subtitle.displayName = 'PageHero.Subtitle'

type ProgressRowProps = {
  value: number
  /**
   * `inline`: barra + rótulo à direita na mesma linha (rótulo costuma ser só
   * o percentual). `stacked`: rótulo e % na 1ª linha, barra full-width na 2ª
   * — mesmo layout de `ProgressBar.Row`, com tipografia do hero.
   */
  layout?: 'inline' | 'stacked'
  /** inline: texto à direita da barra. stacked: texto à esquerda na 1ª linha. */
  label: ReactNode
  /** Conteúdo extra na célula esquerda (stacked) — recompensas, CardHint, etc. */
  slot?: ReactNode
  variant?: ProgressBarVariant
  size?: ProgressBarSize
}

const ProgressRow = ({
  value,
  layout = 'inline',
  label,
  slot,
  variant = 'primary',
  size = 'sm'
}: ProgressRowProps) => {
  const clamped = Math.min(100, Math.max(0, value))

  if (layout === 'stacked') {
    return (
      <div className={styles.heroProgressStack}>
        <div className={styles.heroProgressStackHeader}>
          <div className={styles.heroProgressStackLeading}>
            {slot}
            <span className={styles.heroProgressStackLabel}>{label}</span>
          </div>
          <span className={styles.heroPercent}>{clamped}%</span>
        </div>
        <ProgressBar value={clamped} variant={variant} size={size} />
      </div>
    )
  }

  return (
    <div className={styles.heroProgressRow}>
      <ProgressBar
        value={clamped}
        variant={variant}
        size={size}
        className={styles.heroProgressTrack}
      />
      <span className={styles.heroPercent}>{label}</span>
    </div>
  )
}
ProgressRow.displayName = 'PageHero.ProgressRow'

// ── Props ──────────────────────────────────────────────────────

type PageHeroCardProps = {
  /** Visual card hero — usado abaixo de PageHeader. */
  variant?: 'default' | 'detail' | 'light'
  art: string
  artFit?: 'cover' | 'contain'
  children: ReactNode
  className?: string
  backgroundImage?: string
  overlay?: ReactNode
  // full-only props not allowed in card mode
  title?: never
  onBack?: never
  action?: never
  artClassName?: never
}

type PageHeroFullProps = {
  /** Full-bleed gradient hero — substitui PageHeader. Requer Screen.Context fullBleed no pai. */
  variant: 'full'
  title: string
  action?: ReactNode
  children?: ReactNode
  /** Faixa de stats full-width abaixo do content row (separada por borda). */
  statBar?: ReactNode
  art?: string
  artClassName?: string
  /** Menos padding e arte menor — telas com conteúdo logo abaixo (ex.: feed). */
  compact?: boolean
  className?: string
  // card-only props not allowed in full mode
  backgroundImage?: never
  artFit?: never
  overlay?: never
}

export type PageHeroProps = PageHeroCardProps | PageHeroFullProps

// ── Component ─────────────────────────────────────────────────

const PageHeroFull = ({
  title,
  action,
  children,
  statBar,
  art,
  artClassName,
  compact = false,
  className
}: PageHeroFullProps) => {
  return (
    <>
      <PageMeta title={title} />
      <div style={{ background: 'var(--gradient-hero)' }} className={className}>
        <div className="mx-auto w-full max-w-2xl">
          {/* Nav row */}
          <div
            className={clsx(
              'flex items-center gap-3 px-4',
              compact ? 'pb-3 pt-3' : 'pb-1 pt-4'
            )}
          >
            <PageBackButton variant="immersive" />
            <h1
              className={clsx(
                'm-0 flex-1 font-bold leading-tight text-white',
                compact ? 'text-base' : 'text-lg'
              )}
            >
              {title}
            </h1>
            {action && <div className="shrink-0">{action}</div>}
          </div>

          {/* Content row */}
          {(children || art) && (
            <div
              className={clsx(
                'flex items-end gap-3 px-4',
                compact ? 'pt-0' : 'px-5 pt-1'
              )}
            >
              {children && (
                <div className={clsx('min-w-0 flex-1', compact ? 'pb-2' : 'pb-4')}>
                  {children}
                </div>
              )}
              {art && (
                <div
                  className={clsx(
                    'shrink-0',
                    compact ? 'w-14' : 'w-24',
                    artClassName
                  )}
                >
                  <img src={art} alt="" aria-hidden className="w-full object-contain" />
                </div>
              )}
            </div>
          )}

          {/* Stat bar — full-width, sem padding (FeedStatBar controla o próprio) */}
          {statBar && (
            <div className="border-t border-white/10">
              {statBar}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export const PageHero = (props: PageHeroProps) => {
  if (props.variant === 'full') return <PageHeroFull {...props} />

  const { art, children, className, backgroundImage, artFit, variant, overlay } = props

  return (
    <div
      className={clsx(
        styles.hero,
        variant === 'detail' && styles.heroDetail,
        variant === 'light' && styles.heroLight,
        backgroundImage && styles.heroWithImage,
        className
      )}
      style={
        backgroundImage
          ? ({ '--hero-bg-image': `url(${backgroundImage})` } as CSSProperties)
          : undefined
      }
    >
      {overlay && <div className={styles.heroOverlay}>{overlay}</div>}
      <div
        className={clsx(
          styles.heroInner,
          variant === 'detail' && styles.heroInnerDetail
        )}
      >
        <div className={styles.heroArtWrap}>
          <img
            src={art}
            alt=""
            className={clsx(
              styles.heroArt,
              artFit === 'contain' && styles.heroArtContain
            )}
            aria-hidden
          />
        </div>
        <div
          className={clsx(styles.heroContent, overlay && styles.heroContentWithOverlay)}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

PageHero.Title = Title
PageHero.Subtitle = Subtitle
PageHero.ProgressRow = ProgressRow
PageHero.displayName = 'PageHero'
